/*
    Setup component, lists steps that user can take to connect the incubator to internet
*/


import React, { useState } from 'react';
import Navbar from "../components/Navbar";
import { Box, Button, Typography, Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, TextField, DialogActions } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { sendFeedback } from '../utils/apiClient';

function Setup() {
    const [open, setOpen] = useState(false);
    const [feedback, setFeedback] = useState('');

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleFeedbackChange = (event) => {
        setFeedback(event.target.value);
    };

    const handleSubmitFeedback = () => {
        if (feedback.length !== 0) {
            sendFeedback(feedback)
                .catch((err) => console.error("Error sending feedback:", err));
        }
        handleClose();
    };

    return (
        <div className='setup-container'>
            <Navbar />
            <Box sx={{ padding: 4, margin: 'auto', maxWidth: '900px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h4" gutterBottom>
                        Setting up MIRA
                    </Typography>
                    <Button variant="contained" color="primary" onClick={handleClickOpen} sx={{ borderRadius: 5 }}>
                        Help
                    </Button>
                </Box>

                <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
                    This guide presents the steps for setting up your MIRA device. Follow the instructions to connect your device to the network and start monitoring your petri dishes.
                </Typography>

                <Accordion sx={{ backgroundColor: '#222', color: 'white', mb: 2, boxShadow: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                        <Typography variant="h6">I. Setup WiFi without Monitor</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" gutterBottom>
                            1. Boot up the device, and it should broadcast its network "MiraNetwork-&#60;DeviceName&#62;". Password: tritonbio.
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            2. Connect to the network via your laptop or mobile. Open a browser and type: <strong>192.168.1.50:5000</strong>.
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            3. Login using your registered credentials (the same you use on the website).
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            4. Enter your WiFi credentials. Choose from available options (SSID/Password, unsecured, or SSID/Password with NETID).
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            5. Name your device (optional). Once you've filled in the details, click Connect.
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            6. You should be disconnected from the "MiraNetwork", and the connection will be shown on the device screen.
                            If the device doesn't connect to WiFi, then reboot the device and repeat the steps. If successful, you can start using our IoT setup or connect via Raspberry Pi Connect.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion sx={{ backgroundColor: '#222', color: 'white', mb: 2, boxShadow: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                        <Typography variant="h6">II. Using HDMI Cable or Display</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" gutterBottom>
                            1. Connect MIRA to a display using an HDMI cable to configure your WiFi network through the GUI.
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            2. Once connected, go to:
                        </Typography>
                        <Typography variant="body2" component="ul">
                            <li>Wifi Menu → Advanced Options → Edit Connections</li>
                            <li>Select your network and click the settings icon.</li>
                            <li>Go to General → Enable Connect automatically with priority and save the settings.</li>
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            3. You can now use your device via HDMI or connect to it using Raspberry Pi Connect with your MIRA credentials.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion sx={{ backgroundColor: '#222', color: 'white', mb: 2, boxShadow: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                        <Typography variant="h6">III. Using Ethernet Cable</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" gutterBottom>
                            If your workspace has an available Ethernet connection, you can connect your device via Ethernet and access it through Raspberry Pi Connect to run experiments.
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Visit the following link to sign in: <a href="https://connect.raspberrypi.com/sign-in" target="_blank" rel="noopener noreferrer" style={{ color: '#64b5f6' }}>https://connect.raspberrypi.com/sign-in</a>.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            </Box>

            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        backgroundColor: '#333',
                        color: 'white',
                        border: '1px solid #555',
                        width: '500px',
                    }
                }}
            >
                <DialogTitle sx={{ color: 'white' }}>Submit Feedback</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Enter your feedback"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={feedback}
                        multiline
                        rows={4}
                        onChange={handleFeedbackChange}
                        InputLabelProps={{
                            style: { color: 'white' }
                        }}
                        InputProps={{
                            style: { color: 'white' },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'white',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'gray',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'white',
                                }
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="secondary" sx={{ color: 'gray' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmitFeedback} color="primary" sx={{ color: 'white' }}>
                        Send
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Setup;
